'use client';

import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
} from 'react';
import { useIdlePopup } from './useIdlePopup';
import { useModal } from 'components/shared/Modal';
import { TypeformSnippet } from 'components/shared/TypeformSnippet';
import { CustomImage } from 'components/shared/custom-image';
import { useLocationMatch } from 'hooks/navigation/useLocationMatch';
import {
  BANGALORE_EXPERIENCE_CENTRE,
  BANGALORE_LOCATION_TARGET,
  BANGALORE_UBER_DEEPLINK,
  DEFAULT_PRODUCT,
  DEFAULT_TYPEFORM_ID,
  RING_AIR_POPUP_IMAGE,
  RING_PRO_POPUP_IMAGE,
  type ClaimSizingKitProduct,
} from './constants';
import { useTranslations } from 'next-intl';
import styles from './index.module.css';

type ClaimSizingKitPopupProps = {
  delayMs?: number;
  typeformId?: string;
  /** Region code passed as utm_medium tracking param */
  region?: string;
  product?: ClaimSizingKitProduct;
};

export type ClaimSizingKitPopupHandle = {
  cancelPopup: () => void;
};

const CtaChevron = () => (
  <svg
    className={styles.chevronArrow}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4.5 2.5L8 6L4.5 9.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ClaimSizingKitPopup = forwardRef<
  ClaimSizingKitPopupHandle,
  ClaimSizingKitPopupProps
>(
  (
    {
      delayMs = 60_000,
      typeformId = DEFAULT_TYPEFORM_ID,
      region,
      product = DEFAULT_PRODUCT,
    },
    ref,
  ) => {
    const {
      status: locationStatus,
      matched: isBangaloreLocation,
      method: matchMethod,
    } = useLocationMatch(BANGALORE_LOCATION_TARGET);
    const isBangaloreUber = product === 'ring_air' && isBangaloreLocation;
    const variant = isBangaloreUber ? 'bangalore_uber' : 'sizing_kit';
    const { isVisible, cancel, dismiss, markClaimed } = useIdlePopup({
      delayMs,
      region,
      product,
      variant,
      matchMethod,
      enabled: product === 'ring_pro' || locationStatus === 'resolved',
    });
    const t = useTranslations('common');

    useImperativeHandle(ref, () => ({ cancelPopup: cancel }), [cancel]);
    const ModalClient = useModal();

    const handleSizingKitAccept = useCallback(() => {
      markClaimed();
      const regionMedium = region?.toUpperCase();
      const trackingParams = {
        utm_source: 'websk',
        utm_content: product,
        product,
        ...(regionMedium ? { utm_medium: regionMedium } : {}),
      };
      ModalClient.set(
        <div className={styles.typeformModal}>
          <TypeformSnippet
            typeformId={typeformId}
            frameTitle={t(
              'claimSizingKitPopup.typeformSnippet.frameTitle.claimYourFree',
            )}
            className={styles.typeformContainer}
            trackingParams={trackingParams}
          />
        </div>,
      );
      ModalClient.setCloseButtonTheme('light');
      ModalClient.show();
    }, [typeformId, region, product, markClaimed, ModalClient, t]);

    const handleExternalCta = useCallback(
      (url: string) => {
        markClaimed();
        if (typeof window !== 'undefined') {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      },
      [markClaimed],
    );

    if (!isVisible) return null;

    const title = isBangaloreUber
      ? t('claimSizingKitPopup.bangaloreUber.title.comeTryItOn')
      : t('claimSizingKitPopup.title.claimYourFree');
    const subtitle = isBangaloreUber
      ? t('claimSizingKitPopup.bangaloreUber.subtitle.bestWayToPick')
      : t('claimSizingKitPopup.subtitle.sureAboutYour');
    const isProKit = product === 'ring_pro';
    const imageSrc = isBangaloreUber
      ? BANGALORE_EXPERIENCE_CENTRE.imageSrc
      : isProKit
      ? RING_PRO_POPUP_IMAGE
      : RING_AIR_POPUP_IMAGE;
    const imageAlt = isBangaloreUber
      ? t('claimSizingKitPopup.bangaloreUber.kitImage.alt.experienceCentre')
      : isProKit
      ? t('claimSizingKitPopup.kitImage.alt.ultrahumanRingPro')
      : t('claimSizingKitPopup.kitImage.alt.ultrahumanRingAir');

    return (
      <div className={styles.overlay} onClick={dismiss}>
        <div
          className={`${styles.popupCard} ${
            isBangaloreUber ? styles.popupCardHero : styles.popupCardDefault
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="claim-sizing-kit-popup-title"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className={`${styles.closeButton} ${
              isBangaloreUber
                ? styles.closeButtonHero
                : styles.closeButtonDefault
            }`}
            onClick={dismiss}
            aria-label={t('claimSizingKitPopup.closeButton.ariaLabel.close')}
          >
            {isBangaloreUber ? (
              <svg
                className={styles.closeIcon}
                aria-hidden="true"
                viewBox="0 0 12 12"
              >
                <path
                  d="M2.5 2.5L9.5 9.5M9.5 2.5L2.5 9.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <img
                src="/Close.svg"
                alt={t('claimSizingKitPopup.image.alt.close')}
                width={32}
                height={32}
              />
            )}
          </button>
          {isBangaloreUber ? (
            <>
              <div className={styles.heroImageWrap}>
                <CustomImage
                  className={`${styles.kitImage} ${styles.kitImageFull}`}
                  src={imageSrc}
                  alt={imageAlt}
                  width={744}
                  height={420}
                  sizes="(max-width: 420px) 90vw, 372px"
                />
              </div>
              <div className={styles.popupBody}>
                <h2 className={styles.title} id="claim-sizing-kit-popup-title">
                  {title}
                </h2>
                <p className={styles.subtitle}>{subtitle}</p>
                <div className={styles.ctaRow}>
                  <button
                    className={styles.primaryCta}
                    onClick={() =>
                      handleExternalCta(BANGALORE_EXPERIENCE_CENTRE.mapsUrl)
                    }
                  >
                    {t(
                      'claimSizingKitPopup.bangaloreUber.cTALink.getDirections',
                    )}
                    <CtaChevron />
                  </button>
                  <button
                    className={`${styles.ctaLink} ${styles.ctaLinkMobileOnly}`}
                    onClick={() => handleExternalCta(BANGALORE_UBER_DEEPLINK)}
                  >
                    {t('claimSizingKitPopup.bangaloreUber.cTALink.bookMyRide')}
                    <CtaChevron />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <CustomImage
                className={`${styles.kitImage} ${styles.kitImageDefault}${
                  product === 'ring_pro' ? ` ${styles.kitImagePro}` : ''
                }`}
                src={imageSrc}
                alt={imageAlt}
                width={156}
                height={93}
              />
              <h2 className={styles.title} id="claim-sizing-kit-popup-title">
                {title}
              </h2>
              <p className={styles.subtitle}>{subtitle}</p>
              <button
                className={styles.ctaLink}
                onClick={handleSizingKitAccept}
              >
                {t('claimSizingKitPopup.cTALink.wantFreeSizing')}
                <CtaChevron />
              </button>
            </>
          )}
        </div>
      </div>
    );
  },
);
ClaimSizingKitPopup.displayName = 'ClaimSizingKitPopup';
